import React, { useState, useRef, useEffect } from "react";
import classes from "./Navbar.module.css";
import {
  Avatar,
  AppBar,
  Toolbar,
  IconButton,
  Tooltip,
  Badge,
} from "@mui/material";
import {
  Menu as MenuIcon,
  NotificationsNoneOutlined,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { authActions } from "../../../redux/reducers/auth";
import API from "../../../api/api_setting/apiConstants";
import { loaderActions } from "../../../redux/reducers/loader";
import { useLocation } from "react-router-dom";
import { notificationsActions } from "../../../redux/reducers/notifications";
import {
  getNotifications,
  getPmNotifications,
  markNotificationRead,
  markPmNotificationRead,
  deleteNotification,
  deletePmNotification,
} from "../../../api/api_calls/data";
import dayjs from "../../../utils/dayjs";

const NOTIF_PAGE_SIZE = 10;

const Navbar = ({ onMenuClick, isMobile }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifItems, setNotifItems] = useState([]);
  const [notifTotal, setNotifTotal] = useState(0);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifLoadingMore, setNotifLoadingMore] = useState(false);
  const dropdownRef = useRef(null);
  const notifRef = useRef(null);
  const userData = useSelector((state) => state.user);
  const role = useSelector((state) => state.user.role);
  const liveNotifications = useSelector((state) => state.notifications.liveItems);
  const unreadCount = useSelector((state) => state.notifications.unreadCount);
  const navigate = useNavigate();
  const location = useLocation();

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close on route change
  useEffect(() => {
    setOpen(false);
    setNotifOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!role) return;
    setNotifLoading(true);
    const request =
      role === 1 ? getPmNotifications(NOTIF_PAGE_SIZE, 0) : getNotifications(NOTIF_PAGE_SIZE, 0);
    request
      .then((res) => {
        setNotifItems(res?.data?.data?.notifications || []);
        setNotifTotal(res?.data?.data?.countData || 0);
        dispatch(
          notificationsActions.setUnreadCount(res?.data?.data?.unreadCount || 0)
        );
      })
      .catch(() => {})
      .finally(() => setNotifLoading(false));
  }, [role, dispatch]);

  const handleLoadOlder = () => {
    setNotifLoadingMore(true);
    const request =
      role === 1
        ? getPmNotifications(NOTIF_PAGE_SIZE, notifItems.length)
        : getNotifications(NOTIF_PAGE_SIZE, notifItems.length);
    request
      .then((res) => {
        setNotifItems((items) => [...items, ...(res?.data?.data?.notifications || [])]);
        setNotifTotal(res?.data?.data?.countData || 0);
      })
      .catch(() => {})
      .finally(() => setNotifLoadingMore(false));
  };

  const mergedNotifications = [
    ...liveNotifications.filter(
      (live) =>
        !notifItems.some((existing) =>
          existing?._id && live?._id
            ? existing._id === live._id
            : existing?.createdAt === live?.createdAt &&
              existing?.message === live?.message
        )
    ),
    ...notifItems,
  ];
  const hasMoreNotifications = notifItems.length < notifTotal;

  const handleNotifToggle = () => {
    setNotifOpen((prev) => !prev);
  };

  const handleNotifItemClick = (item) => {
    if (item?.isRead || !item?._id) return;

    setNotifItems((items) =>
      items.map((n) => (n._id === item._id ? { ...n, isRead: true } : n))
    );
    dispatch(notificationsActions.markLiveItemRead(item._id));
    dispatch(notificationsActions.decrementUnread());

    const markRead =
      role === 1 ? markPmNotificationRead(item._id) : markNotificationRead(item._id);
    markRead.catch(() => {});
  };

  const handleNotifItemDelete = (item, event) => {
    event.stopPropagation();
    if (!item?._id) return;

    setNotifItems((items) => items.filter((n) => n._id !== item._id));
    setNotifTotal((total) => Math.max(0, total - 1));
    dispatch(notificationsActions.removeLiveItem(item._id));
    if (!item.isRead) dispatch(notificationsActions.decrementUnread());

    const remove =
      role === 1 ? deletePmNotification(item._id) : deleteNotification(item._id);
    remove.catch(() => {});
  };

  // message is stored server-side as a bare fragment (e.g. " has requested for the")
  // meant to sit between userName and serviceName — see databaseLayer/propertyManager.js.
  const formatNotificationText = (item) => {
    const text = `${item?.userName || ""}${item?.message || ""} ${item?.serviceName || ""}`
      .replace(/\s+/g, " ")
      .trim();
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const handleMyAccount = () => {
    setOpen(false);
    navigate("/home/profile");
  };

  const handleLogOut = () => {
    setOpen(false);
    dispatch(loaderActions.toggleLoading(true));
    localStorage.removeItem(API.AUTH_TOKEN);
     window.location.reload();
     navigate("/login");
    dispatch(authActions.logout());
   
    dispatch(loaderActions.toggleLoading(false));
  };

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        background: "white",
        color: "black",
        borderBottom: "1px solid #E0E0E0",
      }}
    >
      <Toolbar className={`${classes.navbar}`}>
        <div className="flex-row align-center">
          {isMobile && (
            <IconButton
              onClick={onMenuClick}
              edge="start"
              color="inherit"
              aria-label="menu"
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <h3 style={{ margin: 0, fontWeight: 600, fontSize: "18px", color: "#222" }}>
            Dashboard
          </h3>
        </div>

        <div className={`flex-row ${classes.actions}`}>
          {/* Notification bell */}
          <div className={classes.dropdown_wrapper} ref={notifRef}>
            <Tooltip title="Notifications">
              <IconButton onClick={handleNotifToggle} className="pointer">
                <Badge badgeContent={unreadCount} color="error" max={99}>
                  <NotificationsNoneOutlined sx={{ color: "#444" }} />
                </Badge>
              </IconButton>
            </Tooltip>

            {notifOpen && (
              <div className={`${classes.dropdown} ${classes.notif_dropdown}`}>
                <div className={classes.dropdown_profile}>
                  <p className={classes.name}>Notifications</p>
                </div>
                <div className={classes.dropdown_divider} />

                {notifLoading ? (
                  <p className={classes.notif_empty}>Loading...</p>
                ) : mergedNotifications.length === 0 ? (
                  <p className={classes.notif_empty}>No notifications yet</p>
                ) : (
                  <>
                    {mergedNotifications.map((item, i) => (
                      <div
                        key={item?._id || `${item?.createdAt}-${i}`}
                        onClick={() => handleNotifItemClick(item)}
                        className={`${classes.notif_item} ${
                          !item?.isRead ? classes.notif_item_unread : ""
                        }`}
                      >
                        {!item?.isRead && <span className={classes.notif_dot} />}
                        <div className={classes.notif_item_body}>
                          <p className={classes.notif_message}>
                            {formatNotificationText(item)}
                          </p>
                          <p className={classes.notif_time}>
                            {item?.createdAt ? dayjs(item.createdAt).format("lll") : ""}
                          </p>
                        </div>
                        <IconButton
                          size="small"
                          className={classes.notif_delete}
                          onClick={(e) => handleNotifItemDelete(item, e)}
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </div>
                    ))}
                    {hasMoreNotifications && (
                      <button
                        className={classes.notif_load_older}
                        onClick={handleLoadOlder}
                        disabled={notifLoadingMore}
                      >
                        {notifLoadingMore ? "Loading..." : "Load older"}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Custom dropdown */}
          <div className={classes.dropdown_wrapper} ref={dropdownRef}>
            <Tooltip title="Account Settings">
              <Avatar
                src={userData.profileImg}
                variant="rounded"
                onClick={() => setOpen((prev) => !prev)}
                className="pointer"
              />
            </Tooltip>

            {open && (
              <div className={classes.dropdown}>
                {/* Profile header */}
                <div className={classes.dropdown_profile}>
                  <Avatar
                    src={userData.profileImg}
                    variant="rounded"
                    sx={{ width: 36, height: 36 }}
                  />
                  <div className={classes.dropdown_profile_info}>
                    <p className={classes.name}>{userData.name}</p>
                    <p className={classes.email}>{userData.email}</p>
                  </div>
                </div>

                <div className={classes.dropdown_divider} />

                <button
                  className={classes.dropdown_item}
                  onClick={handleMyAccount}
                >
                  Account Settings
                </button>

                <div className={classes.dropdown_divider} />

                <button
                  className={`${classes.dropdown_item} ${classes.dropdown_item_logout}`}
                  onClick={handleLogOut}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                    <polyline points="16 17 21 12 16 7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;